import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { commissionDetailByPaymentId, getCommissionProfitDetailByInvoiceId, updateCommissionProfitCosts, UpdateCommissionProfitCostsRequest } from "../../../service/CommissionService";
import { useAppSelector } from "../redux-hooks";

export type CommissionDetailInfoDTO = {
    commissionId: number;
    commissionAmount: number;
    commissionDate: string;
    commissionComments: string;
    commissionAgencyType: string;
    commissionDocumentId: number;
    fileId: string;
}

export type CommissionProfitDetailInfo = {
  invoiceId: number;
  purchaseAndBillEnabled: boolean;
  cogsAmount: number;
  shippingChargesActual: number;
  v3TotalCost: number;
  invoiceTotal: number;
  agencyCommissionAmount: number;
  overrideCommissionAmount: number;
  grossProfit: number;
  finalProfit: number;
};

type SalesDetailsState = {
  commissionListByPaymentId: CommissionDetailInfoDTO[] | null;
  commissionProfitDetail: CommissionProfitDetailInfo | null;
  status: LoadingType;
  commissionProfitDetailStatus: LoadingType;
  updateCommissionProfitCostsStatus: LoadingType;
}

const initialState: SalesDetailsState = {
  commissionListByPaymentId: null,
  commissionProfitDetail: null,
  status: "idle",
  commissionProfitDetailStatus: "idle",
  updateCommissionProfitCostsStatus: "idle",
};

export const getCommissionListByPaymentId = createAsyncThunk("getCommissionListByPaymentId", async (paymentId: number) => {
  const response = await commissionDetailByPaymentId(paymentId);
  const v3Response = response.data;
  return v3Response;
});

export const fetchCommissionProfitDetail = createAsyncThunk(
  "fetchCommissionProfitDetail",
  async (invoiceId: number) => {
    const response = await getCommissionProfitDetailByInvoiceId(invoiceId);
    return response.data;
  }
);

export const saveCommissionProfitCosts = createAsyncThunk(
  "saveCommissionProfitCosts",
  async (payload: UpdateCommissionProfitCostsRequest) => {
    const response = await updateCommissionProfitCosts(payload);
    return response.data;
  }
);

const CommissionSlice = createSlice({
  name: "CommissionSlice",
  initialState,
  reducers: {
    resetCommissionProfitDetail: (state) => {
      state.commissionProfitDetail = null;
      state.commissionProfitDetailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCommissionListByPaymentId.pending, (state) => {
        state.status = "loading"
      })
      .addCase(getCommissionListByPaymentId.fulfilled, (state, action) => {
        state.status = "success";
        // state.commissionListByPaymentId = action.payload.data;               // CS-1984: guarded below
        state.commissionListByPaymentId = action.payload?.data ?? [];           // CS-1984
      })
      .addCase(getCommissionListByPaymentId.rejected, (state) => {
        state.status = "error"
      })
      .addCase(fetchCommissionProfitDetail.pending, (state) => {
        state.commissionProfitDetailStatus = "loading";
      })
      .addCase(fetchCommissionProfitDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.commissionProfitDetailStatus = "success";
        state.commissionProfitDetail = action.payload?.data ?? null;
      })
      .addCase(fetchCommissionProfitDetail.rejected, (state) => {
        state.commissionProfitDetailStatus = "error";
        state.commissionProfitDetail = null;
      })
      .addCase(saveCommissionProfitCosts.pending, (state) => {
        state.updateCommissionProfitCostsStatus = "loading";
      })
      .addCase(saveCommissionProfitCosts.fulfilled, (state, action: PayloadAction<any>) => {
        state.updateCommissionProfitCostsStatus = "success";
        state.commissionProfitDetail = action.payload?.data ?? state.commissionProfitDetail;
      })
      .addCase(saveCommissionProfitCosts.rejected, (state) => {
        state.updateCommissionProfitCostsStatus = "error";
      });
  },
});

export const { resetCommissionProfitDetail } = CommissionSlice.actions;
export const useCommissionDetailSlice = () => useAppSelector((state) => state.commissionSlice);
export default CommissionSlice.reducer;
